import React, { useRef, useState } from 'react';
import { Box, Button, IconButton, Typography, Collapse, LinearProgress, Tooltip } from '@mui/material';
import { GridCloseIcon } from '@mui/x-data-grid';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faCheckCircle, faExclamationCircle, faCircle, faPauseCircle } from '@fortawesome/free-solid-svg-icons';
import FileIcon from './FileIcon';
import { getMimeType, formatSize } from './file-utils';

library.add(faChevronDown, faChevronUp, faCheckCircle, faExclamationCircle, faCircle, faPauseCircle);

// Status tiap item: "queued" | "uploading" | "retrying" | "paused" | "done" | "error" | "canceled"

function ItemStatusIcon({ status, progress }) {
    if (status === 'done') {
        return <FontAwesomeIcon icon="check-circle" style={{ color: '#1a90ff' }} />;
    }
    if (status === 'error') {
        return <FontAwesomeIcon icon="exclamation-circle" style={{ color: '#d32f2f' }} />;
    }
    if (status === 'paused' || status === 'retrying') {
        return <FontAwesomeIcon icon="pause-circle" style={{ color: '#f9a825' }} />;
    }
    if (status === 'uploading') {
        return (
            <Box sx={{ position: 'relative', display: 'inline-flex', width: 20, height: 20 }}>
                <svg viewBox="0 0 36 36" width="20" height="20">
                    <path
                        d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="#e0e0e0"
                        strokeWidth="3"
                    />
                    <path
                        d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="#1a90ff"
                        strokeWidth="3"
                        strokeDasharray={`${progress || 0}, 100`}
                    />
                </svg>
            </Box>
        );
    }
    // queued / canceled
    return <FontAwesomeIcon icon="circle" style={{ color: '#e0e0e0' }} />;
}

/**
 * Panel upload multi-file bergaya Google Drive, sekarang dengan upload yang
 * bisa DIJEDA dan DILANJUTKAN (potongan yang sudah terkirim tidak diulang).
 *
 * props.items: [{ id, name, size, progress (0-100), loadedBytes, status, error, message, resumable, needsFile }]
 * props.onPause(id) / onResume(id) / onDiscard(id) / onPickFile(id, file)
 * props.onCancelAll: () => void  // batalkan/jeda semua
 * props.onClose: () => void      // tutup panel
 */
function UploadPanel({ items, onCancelAll, onClose, onPause, onResume, onDiscard, onPickFile }) {
    const [collapsed, setCollapsed] = useState(false);
    const pickTargetRef = useRef(null);
    const fileInputRef = useRef(null);

    if (!items || items.length === 0) {
        return null;
    }

    const total = items.length;
    const doneCount = items.filter(i => i.status === 'done').length;
    const errorCount = items.filter(i => i.status === 'error').length;
    const pausedCount = items.filter(i => i.status === 'paused').length;
    const allSettled = items.every(i => ['done', 'error', 'canceled', 'paused'].includes(i.status));

    let headerText;
    if (allSettled) {
        const parts = [`${doneCount} dari ${total} item terupload`];
        if (pausedCount > 0) parts.push(`${pausedCount} dijeda`);
        if (errorCount > 0) parts.push(`${errorCount} gagal`);
        headerText = parts.join(', ');
    } else {
        headerText = `Mengupload ${doneCount} dari ${total} item`;
    }

    // Item yang sudah selesai terupload langsung disembunyikan dari daftar,
    // supaya yang terlihat cuma yang masih diproses. Error/dijeda tetap tampil.
    const visibleItems = items.filter(i => i.status !== 'done');

    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                width: 400,
                maxWidth: 'calc(100vw - 32px)',
                bgcolor: 'background.paper',
                boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                borderRadius: '8px',
                overflow: 'hidden',
                zIndex: 1300,
                border: '1px solid rgba(0,0,0,0.12)',
            }}
        >
            <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => {
                    const file = e.target.files[0];
                    e.target.value = null;
                    if (file && pickTargetRef.current !== null) onPickFile(pickTargetRef.current, file);
                }}
            />
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.25,
                    bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#2b2b2b' : '#f1f3f4'),
                }}
            >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {headerText}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={collapsed ? 'Perluas' : 'Ciutkan'}>
                        <IconButton size="small" onClick={() => setCollapsed(c => !c)}>
                            <FontAwesomeIcon icon={collapsed ? 'chevron-up' : 'chevron-down'} size="xs" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={allSettled ? 'Tutup' : 'Jeda semua & tutup'}>
                        <IconButton
                            size="small"
                            onClick={() => {
                                if (!allSettled && onCancelAll) {
                                    onCancelAll();
                                }
                                if (onClose) onClose();
                            }}
                        >
                            <GridCloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <Collapse in={!collapsed}>
                <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
                    {visibleItems.map((item) => (
                        <Box
                            key={item.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                px: 2,
                                py: 1,
                                borderTop: '1px solid rgba(0,0,0,0.06)',
                            }}
                        >
                            <Box sx={{ opacity: item.status === 'error' ? 0.5 : 1 }}>
                                <FileIcon mimeType={getMimeType(item.name)} />
                            </Box>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    noWrap
                                    title={item.name}
                                    sx={{ color: item.status === 'error' ? 'error.main' : 'text.primary' }}
                                >
                                    {item.name}
                                </Typography>
                                {(item.status === 'uploading' || item.status === 'paused' || item.status === 'retrying') && (
                                    <LinearProgress
                                        variant="determinate"
                                        value={item.progress || 0}
                                        color={item.status === 'uploading' ? 'primary' : 'warning'}
                                        sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                                    />
                                )}
                                <Typography variant="caption" color={item.status === 'error' ? 'error' : 'text.secondary'} component="div" sx={{ lineHeight: 1.35 }}>
                                    {item.status === 'uploading' && (item.loadedBytes != null && item.size != null
                                        ? `${formatSize(item.loadedBytes)} dari ${formatSize(item.size)}`
                                        : `${item.progress || 0}%`)}
                                    {item.status === 'retrying' && (item.message || 'Koneksi putus, mencoba lagi otomatis...')}
                                    {item.status === 'paused' && (item.needsFile
                                        ? `Tertunda ${item.progress || 0}%: pilih file yang sama untuk melanjutkan`
                                        : `Dijeda di ${item.progress || 0}%, bisa dilanjutkan`)}
                                    {item.status === 'error' && `${item.error || 'Gagal mengupload'}${item.resumable ? ' (bisa dilanjutkan)' : ''}`}
                                    {item.status === 'queued' && formatSize(item.size)}
                                    {item.status === 'canceled' && 'Dibatalkan'}
                                </Typography>
                                {(item.status === 'paused' || (item.status === 'error' && item.resumable)) && (
                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                        {item.needsFile ? (
                                            <Button size="small" variant="outlined" onClick={() => { pickTargetRef.current = item.id; fileInputRef.current.click(); }}>
                                                Pilih file
                                            </Button>
                                        ) : (
                                            <Button size="small" variant="outlined" startIcon={<PlayArrowIcon />} onClick={() => onResume(item.id)}>
                                                Lanjutkan
                                            </Button>
                                        )}
                                        <Button size="small" color="error" onClick={() => onDiscard(item.id)}>Buang</Button>
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                {item.status === 'uploading' && onPause && (
                                    <Tooltip title="Jeda">
                                        <IconButton size="small" onClick={() => onPause(item.id)}><PauseIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                )}
                                <ItemStatusIcon status={item.status} progress={item.progress} />
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}

export default UploadPanel;
